import { createTheme, keyframes } from '@mui/material/styles';

const smoothPulse = keyframes`
  0% {
    box-shadow: 0 0 10px 0px rgba(138, 63, 252, 0.7);
  }
  50% {
    box-shadow: 0 0 20px 4px rgba(138, 63, 252, 1);
  }
  100% {
    box-shadow: 0 0 10px 0px rgba(138, 63, 252, 0.7);
  }
`;

const getTheme = (themeMode: 'light' | 'dark') =>
    createTheme({
        palette: {
            mode: themeMode,
            primary: {
                main: '#8A3FFC',
                contrastText: '#fff',
            },
            secondary: {
                main: '#E63946',
                contrastText: '#fff',
            },
            background: {
                default: themeMode === 'dark' ? '#121212' : '#F7F7F7',
                paper: themeMode === 'dark' ? '#1e1e1e' : '#fff',
            },
            text: {
                primary: themeMode === 'dark' ? '#ffffff' : '#1D3557',
                secondary: themeMode === 'dark' ? '#B0BEC5' : '#457B9D',
            },
        },
        typography: {
            fontFamily: '"Poppins", "Roboto", sans-serif',
            h6: {
                fontWeight: 700,
                color: themeMode === 'dark' ? '#ffffff' : '#1D3557',
            },
            button: {
                fontWeight: 600,
                color: '#fff',
            },
            body1: {
                color: themeMode === 'dark' ? '#ffffff' : '#1D3557',
            },
        },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#8A3FFC',
                        boxShadow: 'none',
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '12px',
                        textTransform: 'none',
                        padding: '10px 20px',
                        backgroundColor: '#8A3FFC',
                        color: '#fff',
                        transition: 'background 0.3s ease',
                        '&:hover': {
                            backgroundColor: '#7A2EDC',
                        },
                    },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        borderRadius: '16px',
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        background: themeMode === 'dark'
                            ? 'linear-gradient(145deg, #1e1e1e, #2a2a2a)'
                            : 'linear-gradient(145deg, #ffffff, #f0f0f0)',
                        boxShadow: '0 0 10px rgba(138, 63, 252, 0.5)',
                        '&:hover': {
                            transform: 'translateY(-5px)',
                            animation: `${smoothPulse} 2s infinite`,
                        },
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        color: themeMode === 'dark' ? '#ffffff' : '#1D3557',
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        borderRadius: '16px',
                        background: themeMode === 'dark' ? '#292929' : '#fff',
                        boxShadow: themeMode === 'dark'
                            ? 'inset 4px 4px 8px #1e1e1e, inset -4px -4px 8px #323232'
                            : 'inset 4px 4px 8px #e0e0e0, inset -4px -4px 8px #ffffff',
                        '& .MuiInputBase-root': {
                            color: themeMode === 'dark' ? '#ffffff' : '#1D3557',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderRadius: '16px',
                            borderColor: themeMode === 'dark' ? '#555' : '#ccc',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: themeMode === 'dark' ? '#777' : '#8A3FFC',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#8A3FFC',
                        },
                    },
                },
            },
            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        paddingLeft: '16px',
                        '&:hover': {
                            backgroundColor: themeMode === 'dark' ? '#333' : '#f5f5f5',
                        },
                        '&.Mui-selected': {
                            backgroundColor: '#8A3FFC',
                            color: '#fff',
                        },
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        borderRadius: 12,
                    },
                },
            },
            MuiAutocomplete: {
                styleOverrides: {
                    listbox: {
                        '& .MuiAutocomplete-option': {
                            color: themeMode === 'dark' ? '#ffffff' : '#1D3557',
                            '&[aria-selected="true"]': {
                                backgroundColor: '#8A3FFC',
                                color: '#fff',
                            },
                            '&.Mui-focused': {
                                backgroundColor: '#8A3FFC',
                                color: '#fff',
                            },
                        },
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        borderRadius: '16px',
                        backgroundColor: themeMode === 'dark' ? '#292929' : '#ffffff',
                        boxShadow: themeMode === 'dark'
                            ? '0px 4px 12px rgba(0, 0, 0, 0.4)'
                            : '0px 4px 12px rgba(0, 0, 0, 0.1)',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#8A3FFC',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#8A3FFC',
                        },
                        '& .MuiSelect-icon': {
                            color: themeMode === 'dark' ? '#ffffff' : '#1D3557',
                        },
                    },
                },
            },
        },
    });

export default getTheme; 